import React, { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import ButtonSwitchMulti from '../../../../components/actions/ButtonSwitchMulti';
import InputNumber from '../../../../components/inputs/InputNumber';
import Callout from '../../../../helpContext/Callout';
import {
  converterColorDenominator,
  converterColorMode,
  converterGapThreshold,
} from '../store/converter.state';

export type ConverterLocationColorPanelProps = PropsWithChildren & {
  className?: string;
  children?: React.ReactNode;
};

const ConverterLocationColorPanel: React.FC<
  ConverterLocationColorPanelProps
> = props => {
  const { t } = useTranslation('designer');
  const [denominator, setDenominator] = useRecoilState(
    converterColorDenominator,
  );
  const [colorMode, setColorMode] = useRecoilState(converterColorMode);
  const [gapThreshold, setGapThreshold] = useRecoilState(converterGapThreshold);

  const activateDenominatorMode = (value: number) => {
    setDenominator(value === 0 ? null : value);
    setColorMode('denominator');
  };

  const activateSequenceMode = () => {
    setDenominator(null);
    setColorMode('sequence');
  };

  const activateNoColorMode = () => {
    setDenominator(null);
    setColorMode('none');
  };

  const handleGapThresholdChange = (value: number) => {
    setGapThreshold(value < 1 ? 1 : value);
  };

  // Options for the ButtonSwitchMulti component
  const colorModeOptions = [
    { label: t`No Coloring` },
    { label: t`Sequence Gaps` },
    { label: t`Sequence` },
  ];

  // Get the current color mode index
  const getColorModeIndex = () => {
    switch (colorMode) {
      case 'none':
        return 0;
      case 'sequence':
        return 1;
      case 'denominator':
        return 2;
      default:
        return 0;
    }
  };

  // Handle color mode change
  const handleColorModeChange = (index: number) => {
    switch (index) {
      case 0:
        activateNoColorMode();
        break;
      case 1:
        activateSequenceMode();
        break;
      case 2:
        activateDenominatorMode(denominator || 10);
        break;
    }
  };

  return (
    <Callout panelMode type="suggestion">
      <h3 className="mb-2 text-sm font-medium">Location Visualization</h3>

      <div className="mb-4 text-xs">
        <p>Choose how to visualise location patterns:</p>
        <ul className="mt-1 list-disc space-y-1 pl-4">
          <li>
            <strong>Default:</strong> No special coloring
          </li>
          <li>
            <strong>Sequence Gaps:</strong> Highlight gaps in numeric sequence
            larger than threshold
          </li>
          <li>
            <strong>Color Denominator:</strong> Color by location ID
          </li>
        </ul>
      </div>

      <div className="my-4">
        <ButtonSwitchMulti
          buttonType="header"
          autoSize
          options={colorModeOptions}
          selectedIndex={getColorModeIndex()}
          onClick={handleColorModeChange}
        />
      </div>

      <div className="space-y-3">
        {colorMode === 'sequence' && (
          <InputNumber
            title={t`Gap Threshold (ignore gaps ≤ this value)`}
            inputClassName="text-xs"
            className="mt-2"
            value={gapThreshold}
            onChange={handleGapThresholdChange}
            range={[1, null]}
            debounceInterval={100}
          />
        )}

        {colorMode === 'denominator' && (
          <InputNumber
            title={t`Location Color Denominator`}
            inputClassName="text-xs"
            className="mt-2"
            value={denominator}
            onChange={v => activateDenominatorMode(v)}
            range={[1, null]}
            debounceInterval={100}
          />
        )}
      </div>
      {/* 
      <div className="mt-4 rounded bg-gray-100 p-2 text-xs dark:bg-gray-800">
        <p className="font-medium">Current mode: {colorMode}</p>

        {colorMode === 'sequence' && (
          <div className="mt-1 space-y-1">
            <p className="flex items-center">
              <span className="mr-2 inline-block h-3 w-3 bg-red-500"></span>
              Gap {`>`} {gapThreshold} in sequence
            </p>
            <p className="flex items-center">
              <span className="mr-2 inline-block h-3 w-3 bg-blue-500"></span>{' '}
              Even numbers
            </p>
            <p className="flex items-center">
              <span className="mr-2 inline-block h-3 w-3 bg-green-500"></span>{' '}
              Odd numbers
            </p>
          </div>
        )}

        {colorMode === 'denominator' && denominator && (
          <p className="mt-1">Coloring IDs by {denominator}</p>
        )}

        {colorMode === 'none' && (
          <p className="mt-1">No special coloring applied</p>
        )}
      </div> */}
    </Callout>
  );
};

export default ConverterLocationColorPanel;
